/**
 * storedLesson – what `LessonContext.startLesson` writes to sessionStorage.
 *
 * The player reads its lesson from a single `activeLesson` key, and that key has
 * two writers. Launch paths (ClassLaunchRouter, PartnerDashboard via
 * openLaunchedLessonAsHost, Lessons.jsx, StreetViewLessonCreator) write the FULL
 * payload built by `buildActiveLesson` — images, assets3d, _meta, ttsAudio,
 * language — and then call `startLesson(chapter, topic)`. `startLesson` used to
 * write `{ chapter, topic, startedAt }` unconditionally, so the second write
 * silently replaced the first. That is why students who joined by code had no
 * lesson images: the images were in sessionStorage for a few microseconds and
 * then gone.
 *
 * Fixing each caller would mean four edits that all have to stay in step; the
 * next caller would bring the bug back. Fixing the one writer that destroys data
 * covers all of them: if what is already stored is the SAME lesson, its EXTRA
 * fields (images, assets3d, _meta, ttsAudio, …) are kept. Anything else — a
 * different topic, a different chapter, a different language, nothing stored,
 * or something that is not a lesson at all — is replaced by the thin payload,
 * exactly as before.
 *
 * The caller's `chapter` and `topic` ALWAYS win, even on a match. Not every
 * caller writes the payload first: Content Studio's "Launch Lesson"
 * (LaunchLessonButton) and Spiral hand `startLesson` a freshly built topic and
 * nothing else. An author who edits a script or the MCQs and launches the same
 * topic again, or a Spiral suggestion in another language, must see the new
 * topic — keeping the stored one would show stale content in every player (they
 * all read sessionStorage) and leave sessionStorage disagreeing with
 * LessonContext's React state, which is set from the passed topic. The callers
 * that do write first pass the very objects they stored, so for them "caller
 * wins" and "stored wins" are the same thing.
 *
 * Identity is the pair (chapter_id, topic_id). A topic id alone is not enough:
 * the thin payload must never inherit images and models from a topic that only
 * happens to share an id with one in another chapter. A known language that
 * differs also breaks the match: the kept top-level `ttsAudio`/`language` would
 * otherwise pair narration in one language with a topic in another.
 *
 * Deliberately pure: it takes the raw stored string rather than touching
 * sessionStorage, so it runs under node:test with no DOM and no firebase.
 */

/** The fields `mergeStoredLesson` needs to tell whether two lessons are the same. */
export interface StoredLessonIdentity {
  chapter: { chapter_id: string };
  /** `language` is optional: callers that do not know it never break a match. */
  topic: { topic_id: string; language?: string };
}

/**
 * The payload to store. Always carries the caller's `chapter` and `topic` and a
 * fresh `startedAt`; when the stored payload was for the same lesson, every
 * other field it had (images, assets3d, _meta, ttsAudio, language, …) comes
 * along untouched.
 */
export interface StoredLesson {
  chapter: unknown;
  topic: unknown;
  startedAt: string;
  [key: string]: unknown;
}

/** A plain JSON object — not null, not an array, not a primitive. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parse whatever is in sessionStorage without ever throwing. The key is shared
 * with older builds and other screens, so a truncated write, a hand-edited value
 * or a non-lesson JSON value is possible; each of those simply means "nothing
 * worth keeping".
 */
function parseStored(existingRaw: string | null): Record<string, unknown> | null {
  if (!existingRaw) return null;
  try {
    const parsed: unknown = JSON.parse(existingRaw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * True when `stored` describes the same chapter AND topic as `lesson`, in the
 * same language when both sides name one.
 *
 * The ids must be non-empty strings on both sides: two payloads that are both
 * missing an id are not "the same lesson", they are two unknowns, and keeping
 * one because `undefined === undefined` would resurrect stale content.
 */
function isSameLesson(stored: Record<string, unknown>, lesson: StoredLessonIdentity): boolean {
  const chapter = stored.chapter;
  const topic = stored.topic;
  if (!isRecord(chapter) || !isRecord(topic)) return false;

  const chapterId = lesson.chapter?.chapter_id;
  const topicId = lesson.topic?.topic_id;
  if (typeof chapterId !== 'string' || chapterId === '') return false;
  if (typeof topicId !== 'string' || topicId === '') return false;

  if (chapter.chapter_id !== chapterId || topic.topic_id !== topicId) return false;

  // Only a KNOWN mismatch breaks the match. The payloads written by
  // buildActiveLesson carry `language` at the top level; the thin payload and
  // Lessons.jsx carry it on the topic, hence the fallback. A side that does not
  // say its language is not evidence of a different one.
  const storedLanguage = stored.language ?? topic.language;
  const lessonLanguage = lesson.topic?.language;
  if (typeof storedLanguage === 'string' && typeof lessonLanguage === 'string') {
    return storedLanguage === lessonLanguage;
  }
  return true;
}

/**
 * Decide what `startLesson` should store under `activeLesson`.
 *
 * @param existingRaw What sessionStorage currently holds for the key (null when empty).
 * @param lesson      The chapter and topic `startLesson` was called with.
 * @param startedAt   ISO timestamp for this start; always wins over a stored one.
 * @returns The stored payload's extra fields with the caller's `chapter` and
 *          `topic` and a fresh `startedAt` when it is the same lesson,
 *          otherwise `{ chapter, topic, startedAt }`.
 */
export function mergeStoredLesson(
  existingRaw: string | null,
  lesson: StoredLessonIdentity,
  startedAt: string,
): StoredLesson {
  const stored = parseStored(existingRaw);
  if (stored && isSameLesson(stored, lesson)) {
    // Keep only the stored EXTRAS; the caller's chapter and topic replace the
    // stored ones (see the header for the relaunch-after-edit case this fixes).
    return { ...stored, chapter: lesson.chapter, topic: lesson.topic, startedAt };
  }
  return { chapter: lesson.chapter, topic: lesson.topic, startedAt };
}
